import { EventTypes } from 'redux-segment';
import cookies from 'js-cookie';
import { datadogRum } from '@datadog/browser-rum';
import {
  getCurrentUser,
  signOut as amplifySignOut,
  fetchAuthSession,
  signIn as amplifySignIn,
  AuthError,
} from 'aws-amplify/auth';
import { getAnonymousUserId } from '../libs/user-utils';
import { httpService } from '../../index';
import stringToBase64 from '../libs/encoding-utils';
import { resetMessages } from '../actions/conversations';
import { VISITOR_ROLE } from '../constants/roles';
import config from '../../app-config';
import { history } from '../modules/history';
import { QUOTE_TEMPLATES_READ, RESET } from '../constants/action-types';
import {
  AUTH_TOKEN,
  IS_IMPERSONATION_SESSION,
  USER_ID,
} from '../constants-togather/auth';
import { storeToken } from '../modules/token-storage';

import {
  resetAnalytics,
  trackAlias,
  trackIdentity,
} from '../modules/analytics/analytics';
import { formatPhoneNumberWithGBCountryCode } from '../libs/form-validation-utils';
import {
  isCognitoAuthentication,
  removeSSOLogoutFlag,
} from '../helpers/auth/cognito';
import { COGNITO_IDENTITY_FIELDS } from '../constants-togather/cognito';
import paths from '../constants/paths';
import { ANALYTICS } from '../constants/analytics';
import { captureError } from '../modules/monitoring';
import { client } from '../modules/apollo';
import { FAVOURITE_SUPPLIERS, USERS_ME } from '../queries';
import {
  CREATE_FAVOURITE_SUPPLIER,
  DELETE_FAVOURITE_SUPPLIER,
} from '../mutations';

export const SET_USER = 'platform/users/SET_USER';
const IDENTIFY_USER = 'platform/users/IDENTIFY_USER';
export const LOGOUT_USER = 'platform/users/LOGOUT_USER';
const ADD_FAVOURITE = 'platform/users/ADD_FAVOURITE';
const REMOVE_FAVOURITE = 'platform/users/REMOVE_FAVOURITE';
const STORE_FAVOURITE = 'platform/users/STORE_FAVOURITE';
const SET_USER_TOKEN = 'platform/users/SET_USER_TOKEN';

const LOGIN_NEXT_STEP = {
  COMPLETED: 'DONE',
};

const initialState = {
  type: VISITOR_ROLE.name,
  token: null,
  browsingHistory: [],
  favourites: [],
  storedFavourite: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case STORE_FAVOURITE:
      return {
        ...state,
        storedFavourite: action.payload.supplierId,
      };

    case ADD_FAVOURITE:
      return {
        ...state,
        storedFavourite: null,
        favourites: action.payload,
      };

    case REMOVE_FAVOURITE:
      return {
        ...state,
        favourites: state.favourites
          ? state.favourites.filter(
              (favourite) => action.payload.supplierId !== favourite.uuid
            )
          : [],
      };

    case SET_USER:
      return {
        ...state,
        ...action.payload,
        browsingHistory: action.payload.browsingHistory
          ? [...state.browsingHistory, action.payload.browsingHistory]
          : state.browsingHistory,
      };

    case IDENTIFY_USER:
      return { ...state, ...action.payload };

    case LOGOUT_USER:
      return { ...initialState, browsingHistory: state.browsingHistory };

    case SET_USER_TOKEN:
      return { ...state, token: action.payload };

    default:
      return state;
  }
}

export const addFavouriteAction = (payload, supplierName, fromBrowser) => ({
  type: ADD_FAVOURITE,
  payload,
  meta: {
    analytics: {
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Favourite Added',
        properties: {
          Supplier: supplierName,
          '# of Suppliers Favourited': 1,
          SupplierBrowser: !!fromBrowser,
        },
      },
    },
  },
});

export const removeFavouriteAction = (supplierId, supplierName) => ({
  type: REMOVE_FAVOURITE,
  payload: { supplierId },
  meta: {
    analytics: {
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Favourite Removed',
        properties: {
          Supplier: supplierName,
          '# of Suppliers Favourited': -1,
        },
      },
    },
  },
});

export const storeFavouriteAction = (supplierId) => ({
  type: STORE_FAVOURITE,
  payload: { supplierId },
});

export const setUserAction = (user) => ({ type: SET_USER, payload: user });

export const identifyAction = (user) => ({
  type: IDENTIFY_USER,
  payload: user,
  meta: {
    analytics: {
      eventType: EventTypes.identify,
      eventPayload: {
        userId: user.id,
        traits: {
          phone: formatPhoneNumberWithGBCountryCode(user.phone),
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          'User Type': user.type,
        },
      },
    },
  },
});

export const trackLoginAction = (user) => ({
  type: IDENTIFY_USER,
  payload: user,
  meta: {
    analytics: [
      {
        eventType: EventTypes.identify,
        eventPayload: {
          userId: user.id,
          traits: {
            'First Log In Date': user.state.created,
            'Last Log In Date': user.state.lastLogin,
          },
        },
      },
      {
        eventType: EventTypes.track,
        eventPayload: {
          event: 'Logged in',
        },
      },
    ],
  },
});

export const logoutUserAction = () => ({
  type: LOGOUT_USER,
  payload: null,
  meta: { eventType: EventTypes.reset },
});

export const setUserToken = (token) => ({
  type: SET_USER_TOKEN,
  payload: token,
});

export const setUser = (user) => (dispatch) => {
  dispatch(setUserAction(user));

  if (user) {
    const anonymousUserId = getAnonymousUserId();
    datadogRum.setUser({
      id: user?.id,
      email: user?.email,
      type: user?.type,
      ...(anonymousUserId && { anonymousUserId }),
    });
  }
};

const fetchUsersMe = async (token, { useNewAuth = false } = {}) => {
  const { data } = await client.query({
    query: USERS_ME,
    fetchPolicy: 'no-cache',
    context:
      token || useNewAuth
        ? { ...(token && { authToken: token }), useNewAuth }
        : undefined,
  });

  return data.usersMe;
};

const refetchUser = async (dispatch) => {
  const user = await fetchUsersMe();
  dispatch(setUser(user));

  return user;
};

export const addFavourite =
  (supplierId, userId, supplierName, fromBrowser) => async (dispatch) => {
    await client.mutate({
      mutation: CREATE_FAVOURITE_SUPPLIER,
      variables: { supplierId },
    });

    const user = !userId ? null : await refetchUser(dispatch);

    dispatch(
      addFavouriteAction(user?.favourites ?? [], supplierName, fromBrowser)
    );
  };

export const removeFavourite =
  (supplierId, userId, supplierName) => async (dispatch) => {
    await client.mutate({
      mutation: DELETE_FAVOURITE_SUPPLIER,
      variables: { supplierId },
    });

    if (userId) {
      await refetchUser(dispatch);
    }

    dispatch(removeFavouriteAction(supplierId, supplierName));
  };

export const toggleFavourite =
  (supplierId, user, supplierName) => async (dispatch) => {
    if (!user.id) {
      dispatch(storeFavouriteAction(supplierId));

      history.push(`/login?redirect=${history.location.pathname}`);
      return;
    }

    const isFavourited = user.favourites.some(
      (f) => f && f.uuid === supplierId
    );

    const method = isFavourited ? removeFavourite : addFavourite;

    dispatch(method(supplierId, user.id, supplierName));
  };

const login = (token, storedFavourite) => async (dispatch) => {
  let user = {};

  try {
    user = await fetchUsersMe(token);

    if (token) {
      storeToken(token);
    }
    removeSSOLogoutFlag();
    trackAlias(user.id);
    trackIdentity(user.id, {
      phone: formatPhoneNumberWithGBCountryCode(user.phone),
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      'User Type': user.type,
      'Supplier ID': user?.suppliers?.[0]?.uuid,
      'Vertical Tier 1': user?.suppliers?.[0]?.category_tier1,
      'Vertical Tier 2': user.suppliers?.[0]?.tier2?.[0]?.name,
      State: user?.suppliers?.[0]?.state,
    });

    dispatch(setUser({ token, ...user }));
    dispatch(identifyAction(user));
    dispatch(trackLoginAction(user));

    if (storedFavourite) {
      dispatch(addFavourite(storedFavourite, user.id));
    }
  } catch (error) {
    return { error };
  }

  return user;
};

export const logInWithToken = (token) => async (dispatch) =>
  await login(token)(dispatch);

export const logInWithEmailAddressAndPassword =
  (emailAddress, password, storedFavourite) => async (dispatch) => {
    try {
      const { accessToken: token } = await httpService.request({
        path: '/users/login/',
        method: 'POST',
        headers: {
          Authorization: `Basic ${stringToBase64(
            `${emailAddress.toLowerCase()}:${password}`
          )}`,
        },
        withoutBearer: true,
      });

      return await login(token, storedFavourite)(dispatch);
    } catch (error) {
      return { error };
    }
  };

export const isCognitoUserPasswordAuth = async () => {
  try {
    const user = await getCurrentUser();
    return user?.signInDetails?.authFlowType === 'USER_PASSWORD_AUTH';
  } catch (error) {
    return false;
  }
};

export const logoutUser =
  (callEndpoint = true) =>
  async (dispatch) => {
    const authToken = cookies.get(AUTH_TOKEN);
    resetAnalytics();

    if (isCognitoAuthentication(authToken)) {
      await amplifySignOut();
      // Redirect all Cognito users (both SSO and password auth) to logout page
      window.location.href = paths.LOGOUT;
    } else if (callEndpoint) {
      httpService.request({
        path: '/users/logout',
        method: 'GET',
        withoutBearer: true,
      });
    }

    cookies.remove(AUTH_TOKEN, {
      ...(config.cookieSecure && { secure: config.cookieSecure }),
    });
    cookies.remove(USER_ID, {
      ...(config.cookieSecure && { secure: config.cookieSecure }),
    });
    cookies.remove(IS_IMPERSONATION_SESSION);

    dispatch(logoutUserAction());
    dispatch(resetMessages());
    dispatch({ type: RESET });

    // Clear supplier quote templates
    dispatch({ type: QUOTE_TEMPLATES_READ, payload: [] });
  };

export const fetchUser = (token, extraParams) => async (dispatch) => {
  try {
    const user = await fetchUsersMe(token);

    if (token) {
      storeToken(token);
    }

    dispatch(setUser({ ...(token && { token }), ...user, ...extraParams }));
    dispatch(identifyAction({ ...user }));
  } catch {
    dispatch(logoutUserAction());
  }
};

export const getFavouriteSuppliers = () =>
  client
    .query({
      query: FAVOURITE_SUPPLIERS,
      fetchPolicy: 'network-only',
    })
    .then(({ data }) => data.favouriteSuppliers);

export const loginCognito = async (email, password) => {
  try {
    const result = await amplifySignIn({
      username: email,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });

    return result.nextStep.signInStep;
  } catch (error) {
    if (error?.name === 'UserAlreadyAuthenticatedException') {
      return LOGIN_NEXT_STEP.COMPLETED;
    }

    if (error instanceof AuthError) {
      if (
        error.name === 'UserNotFoundException' ||
        error.name === 'NotAuthorizedException'
      ) {
        throw error;
      }
    }

    captureError(error);
    throw error;
  }
};

export const cognitoSignInCallbackRedux = () => async (dispatch) => {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const idToken = session.tokens?.idToken?.toString();
    const databaseId =
      session.tokens?.idToken?.payload?.[COGNITO_IDENTITY_FIELDS.DATABASE_ID];

    // No Cognito session (e.g. anonymous app bootstrap) — nothing to restore;
    // do NOT force a logout here.
    if (!idToken || !databaseId) {
      return undefined;
    }

    // The id token is the bearer (it carries `custom:databaseId`); the backend
    // derives the user id from it, so no `x-user-id` header is sent.
    const user = await fetchUsersMe(idToken, { useNewAuth: true });
    // Keep the access token in AUTH_TOKEN for now (step 2 removes it for
    // Cognito). No USER_ID cookie — identity comes from the token.
    cookies.set(AUTH_TOKEN, accessToken, { expires: 21 });

    // Update the store with the restored session via SET_USER (handled by the
    // reducer and observed by the realtime io saga). A raw `USER_LOGGED_IN`
    // action is not handled anywhere, so it would leave the user anonymous and
    // the route guards would log them out.
    dispatch(setUser({ token: accessToken, ...user }));

    return user;
  } catch (err) {
    // Do NOT sign out or force a logged-out state here: a transient
    // usersMe failure during auth init / SSO token exchange must not tear
    // down a valid session. The next request retries; explicit logout goes
    // through logoutUser.
    captureError(err);
    return undefined;
  }
};

export const logInWithEmailAndPasswordOrCognito =
  (email, password, storedFavourite) => async (dispatch) => {
    cookies.remove(IS_IMPERSONATION_SESSION);

    try {
      const nextStep = await loginCognito(email, password);

      if (nextStep === LOGIN_NEXT_STEP.COMPLETED) {
        return await dispatch(cognitoSignInCallbackRedux());
      }

      throw new Error('Additional steps required for Cognito login');
    } catch (error) {
      if (error instanceof AuthError) {
        if (error.name === 'UserNotFoundException') {
          return dispatch(
            logInWithEmailAddressAndPassword(email, password, storedFavourite)
          );
        }

        if (error.name === 'NotAuthorizedException') {
          throw 'Your email address or password is incorrect. Please try again.';
        }
      }

      throw error;
    }
  };
